import { TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { FullPageError } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { SpecimenCard, SpecimenSection } from "@/features/system-states/system-state-page";

export default function SysError() {
  return (
    <>
      <SpecimenSection label="Toast notifications">
        <SpecimenCard label="Transient notifications — one per semantic tone">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              onClick={() =>
                toast({ variant: "success", title: "Transfer submitted", description: "TRF-2241 is awaiting approval." })
              }
            >
              Success toast
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                toast({
                  variant: "error",
                  title: "Transfer failed",
                  description: "Destination address failed screening. No funds were moved.",
                })
              }
            >
              Error toast
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                toast({
                  variant: "warning",
                  title: "Balance may be stale",
                  description: "VaultCore has not refreshed within its SLA.",
                })
              }
            >
              Warning toast
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                toast({ variant: "info", title: "Export queued", description: "You will be notified when it is ready." })
              }
            >
              Info toast
            </Button>
          </div>
        </SpecimenCard>
      </SpecimenSection>

      <SpecimenSection label="Inline validation">
        <SpecimenCard label="Field-level errors" className="max-w-[480px]">
          <div className="flex flex-col gap-4">
            <Field label="Destination address" htmlFor="sys-dest" required error="Enter a valid address for the selected network.">
              <Input id="sys-dest" defaultValue="0x8f3c…91ab" error aria-invalid />
            </Field>
            <Field label="Amount" htmlFor="sys-amount" required error="Amount exceeds the available wallet balance.">
              <Input id="sys-amount" defaultValue="450,000.00" error aria-invalid />
            </Field>
          </div>
        </SpecimenCard>
      </SpecimenSection>

      <SpecimenSection label="Alert banners">
        <div className="flex flex-col gap-2">
          <InfoBanner variant="danger" title="Screening provider unavailable" className="mb-0">
            <div className="flex flex-wrap items-center gap-3">
              <span>New transfers are paused until the provider recovers. Retrying every 30 seconds.</span>
              <Button size="sm" variant="secondary" onClick={() => toast({ variant: "info", title: "Retrying…" })}>
                Retry now
              </Button>
            </div>
          </InfoBanner>
          <InfoBanner variant="warning" title="Partial outage" className="mb-0">
            Balance reads are degraded. Last confirmed values are shown with a stale indicator.
          </InfoBanner>
          <InfoBanner variant="info" title="Planned read-only window" className="mb-0">
            A maintenance window is scheduled for 02:00–02:30 UTC. Records stay readable; mutations are disabled.
          </InfoBanner>
        </div>
      </SpecimenSection>

      <SpecimenSection label="Full-page — 503 service unavailable">
        <FullPageError
          code="503"
          codeTone="warning"
          title="Service temporarily unavailable"
          description="We could not reach the custody and signing providers. Your data is safe and no operations were submitted."
          incident="Incident INC-2048 · started 08:42 UTC"
          actions={
            <Button variant="secondary" onClick={() => toast({ variant: "info", title: "Reconnecting…" })}>
              Retry connection
            </Button>
          }
          statuses={[
            { label: "Custody", tone: "warning" },
            { label: "Signing", tone: "danger" },
            { label: "Screening", tone: "success" },
          ]}
        />
      </SpecimenSection>

      <SpecimenSection label="Full-page — 404 unknown route">
        <FullPageError
          code="404"
          codeTone="primary"
          title="Page not found"
          description="The page you requested does not exist or may have been moved. Check the address or return to a known location."
          homeHref="/transfers"
          homeLabel="Back to transfers"
        />
      </SpecimenSection>

      <SpecimenSection label="Inline — unresolved operation">
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Transfer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="font-semibold text-ink">TRF-2230</td>
                <td className="text-muted">12,500.00 USDC</td>
                <td>
                  <Badge variant="danger" dot={false}>
                    OUTCOME_UNKNOWN
                  </Badge>
                </td>
                <td className="text-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <TriangleAlert aria-hidden className="size-3.5 text-danger" /> Broadcast outcome could not be
                    determined — requires manual verification, never a retry.
                  </span>
                </td>
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      </SpecimenSection>
    </>
  );
}
